import { PortalError } from "@/types/portal-error";
import { Service } from "../mainService";
import { FiltrosProgramacoes } from "@/types/eventos/programacao/filtros-programacao";
import { Programacao } from "@/types/eventos/programacao/programacao";
import { AtracaoArtistica } from "@/types/eventos/programacao/atracao-artistica";

const programacaoService = {
  listarProgramacoes,
  filtrarProgramacoes,
  salvarProgramacao,
  buscarProgramacao,
  removerProgramacao,
  checarProgramacao,

  adicionarAtracaoArtistica,
  buscarAtracoes,
  removerAtracaoArtistica,

  baixarModeloImportacaoAtracoes,
  importarAtracoesPlanilha,

  buscarArtistas,
};

function montarArrayDeErros(e: any): PortalError {
  if (e.response.data.erros) return new PortalError(e.response.data.erros);
  if (e.response.data.msg) return new PortalError(e.response.data.msg);
  return new PortalError(e);
}

async function listarProgramacoes(): Promise<any> {
  try {
    const url = `${process.env.VUE_APP_LICITACOES_API}/programacao/listarProgramacoes`;
    const response = await Service.$http.get(url);
    if (response && response.status === 200) {
      return response.data;
    } else {
      console.error("Falha ao listarProgramacoes", response);
      throw new PortalError(
        response.data?.msg || "Ocorreu um erro ao realizar a consulta."
      );
    }
  } catch (e: any) {
    console.error("Falha ao listar programações", e);
    throw new PortalError(e.response.data.msg ?? e);
  }
}

async function filtrarProgramacoes(
  page: number,
  pageSize: number,
  filtros: FiltrosProgramacoes
): Promise<any> {
  try {
    const url = `${
      process.env.VUE_APP_LICITACOES_API
    }/programacao/filtrarProgramacoes?page=${page}&pageSize=${pageSize}${filtros.toQueryString()}`;
    const response = await Service.$http.get(url);
    if (response && response.status === 200) {
      return response.data;
    } else {
      console.error("Falha ao filtrarProgramacoes", response);
      throw new PortalError(
        response.data?.msg ||
          "Ocorreu um erro ao realizar a consulta. Verifique se os filtros de pesquisa aplicados podem ser refinados!"
      );
    }
  } catch (e: any) {
    console.error("Falha ao filtrar programações", e);
    throw new PortalError(e.response.data.msg ?? e);
  }
}

async function salvarProgramacao(prog: Programacao): Promise<Programacao> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_LICITACOES_API + "/programacao/salvarProgramacao",
      prog.toFormData()
    );
    if (response.status === 200) {
      const programacao = Object.assign(new Programacao(), response.data);
      programacao.tratarDadosDaAPI();
      return programacao;
    }
    throw new PortalError("Falha ao salvar dados da programação");
  } catch (e: any) {
    console.error("capotou no service", e);
    if ([400, 403].includes(e.response?.status)) {
      throw montarArrayDeErros(e);
    }
    throw e;
  }
}

async function buscarProgramacao(idProgramacao: string): Promise<Programacao> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API +
      "/programacao/buscarProgramacao/" +
      idProgramacao;
    const response = await Service.$http.get(url);
    const programacao = Object.assign(new Programacao(), response.data);
    return programacao;
  } catch (e: any) {
    return e.response.data;
  }
}

async function checarProgramacao(idProgramacao: string): Promise<{
  ProgramacaoJaUtilizada: boolean;
  IdEvento: number | null;
  NmEvento: string | null;
}> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API +
      "/programacao/checarProgramacao/" +
      idProgramacao;
    const response = await Service.$http.get(url);
    const programacao = Object.assign(new Programacao(), response.data);
    return programacao;
  } catch (e: any) {
    return e.response.data;
  }
}

async function buscarArtistas(): Promise<any[]> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API + "/programacao/buscarArtistas";
    const response = await Service.$http.get(url);
    return response.data;
  } catch (e: any) {
    return e.response.data;
  }
}

async function removerProgramacao(idProg: number): Promise<boolean> {
  try {
    const response = await Service.$http.delete(
      process.env.VUE_APP_LICITACOES_API +
        "/programacao/removerProgramacao/" +
        idProg
    );
    return response.status === 204;
  } catch (e: any) {
    console.error("Erro ao tentar remover programação", e);
    if ([400, 403].includes(e.response.status)) {
      throw new PortalError(e.response.data.erros);
    } else {
      throw e;
    }
  }
}

async function adicionarAtracaoArtistica(
  atr: AtracaoArtistica
): Promise<AtracaoArtistica> {
  if (atr.CdCpfCnpjAtracaoArtistica)
    atr.CdCpfCnpjAtracaoArtistica =
      atr.CdCpfCnpjAtracaoArtistica.replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll("/", "") ?? "";
  if (atr.CdCpfCnpjRepresentanteLegal)
    atr.CdCpfCnpjRepresentanteLegal =
      atr.CdCpfCnpjRepresentanteLegal.replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll("/", "");
  const response = await Service.$http.post(
    process.env.VUE_APP_LICITACOES_API + "/programacao/salvarAtracao",
    atr.toFormData()
  );
  const novaAtr = Object.assign(new AtracaoArtistica(), response.data);
  return novaAtr;
}

async function buscarAtracoes(
  idProgramacao: number,
  page: number,
  size: number,
  texto?: string
): Promise<any> {
  try {
    const url = `${
      process.env.VUE_APP_LICITACOES_API
    }/programacao/buscarAtracoes/${idProgramacao}${
      texto ? `/${texto}` : ""
    }?page=${page}&pageSize=${size}`;
    const response = await Service.$http.get(url);

    if (response && response.status === 200) return response.data;
    else {
      console.error("Falha ao buscar atrações da programação", response);
      return null;
    }
  } catch (e: any) {
    return e.response;
  }
}

async function removerAtracaoArtistica(idAtr: number): Promise<boolean> {
  try {
    const response = await Service.$http.delete(
      process.env.VUE_APP_LICITACOES_API +
        "/programacao/removerAtracao/" +
        idAtr
    );
    return response.status === 204;
  } catch (e: any) {
    console.error("Erro ao tentar remover atração artística", e);
    if ([400, 403].includes(e.response.status)) {
      throw new PortalError(e.response.data.erros);
    } else {
      throw e;
    }
  }
}

async function baixarModeloImportacaoAtracoes(tipo: string): Promise<Blob> {
  const response = await Service.$http.get(
    process.env.VUE_APP_LICITACOES_API + `/programacao/baixarModeloImportacao`,
    { responseType: "blob" }
  );
  if (response && response.status === 200) return response.data;
  console.error(
    "falha ao baixar modelo de importação de atrações artísticas",
    response
  );
  throw new PortalError(response.data.msg ?? response);
}

async function importarAtracoesPlanilha(
  idProg: number,
  data: FormData
): Promise<AtracaoArtistica[]> {
  try {
    const url =
      process.env.VUE_APP_LICITACOES_API +
      `/programacao/importarAtracoesArtisticas/` +
      idProg;
    const response = await Service.$http.post(url, data);
    if (response && response.status === 200) return response.data;
    throw new PortalError(
      response.data.erros ??
        response.data.msg ??
        "Erro ao importar atrações artísticas"
    );
  } catch (e: any) {
    console.error("programacaoService.importarAtracoesPlanilha falhou", e);
    throw montarArrayDeErros(e);
  }
}

export default programacaoService;
